import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { InteractionStatus } from '@azure/msal-browser';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter, firstValueFrom, startWith, take, timeout } from 'rxjs';
import { AppLoadingService } from './app-loading.service';
import { environment } from '../../../../environments/environment';
import { TelemetryService } from '../../../shared/services/telemetry.service';

@Injectable({ providedIn: 'root' })
export class AuthRedirectGuard implements CanActivate {
  constructor(
    private msal: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private loading: AppLoadingService,
    private telemetryService: TelemetryService
  ) {}

  private debug(message: string, details?: unknown): void {
    void message;
    void details;
    void environment;
  }

  async canActivate(): Promise<boolean | UrlTree> {
    this.loading.show('Logger inn og sjekker tilganger...');
    try {
      this.debug('Starting guard check', { path: window.location.pathname });
      await firstValueFrom(
        this.msalBroadcastService.inProgress$.pipe(
          startWith(InteractionStatus.None),
          filter((status) => status === InteractionStatus.None),
          take(1),
          timeout(10000)
        )
      );

      let account = this.msal.instance.getActiveAccount();
      this.debug('Active account before fallback', account ? { homeAccountId: account.homeAccountId, tenantId: account.tenantId } : null);

      if (!account) {
        const accounts = this.msal.instance.getAllAccounts();
        this.debug('Cached accounts found', { count: accounts.length });
        if (accounts.length > 0) {
          this.msal.instance.setActiveAccount(accounts[0]);
          account = accounts[0];
          this.debug('Set cached account as active', { homeAccountId: account.homeAccountId, tenantId: account.tenantId });
        }
      }

      if (!account) {
        this.debug('No account found, redirecting to /auth');
        return this.router.parseUrl('/auth');
      }

      // Force a silent token check on refresh so backend calls can run immediately.
      const tokenResult = await firstValueFrom(
        this.msal.acquireTokenSilent({
          account,
          scopes: environment.azureB2C.msalScopes,
        }).pipe(timeout(10000))
      );
      this.debug('Silent token acquired', {
        accountId: tokenResult.account?.homeAccountId,
        expiresOn: tokenResult.expiresOn?.toISOString?.() ?? null,
        scopes: tokenResult.scopes,
      });

      return true;
    } catch (error: any) {
      this.telemetryService.trackException(error, {
        source: 'AuthRedirectGuard',
        step: 'canActivate',
      });
      this.debug('Guard failed, redirecting to /auth', {
        name: error?.name,
        code: error?.errorCode || error?.code || error?.error?.errorCode || error?.error?.code,
        status: error?.status,
        message: error?.message,
      });
      return this.router.parseUrl('/auth');
    } finally {
      this.loading.hide();
    }
  }
}
